import Menu from '@/global/components/MenuV2'
import Grid from '@/global/components/ui/Grid'
import { TipoOcorrencia } from '@acme/core'
import { Box, Divider } from '@mui/material'
import { FormField, FormFieldCheckbox } from 'global/components/Form'
import IconeAjuda from 'global/components/IconeAjuda'
import { useFormContext } from 'react-hook-form'
import { match } from 'ts-pattern'

export const OcorrenciaForm = () => {
    const { watch } = useFormContext()
    const tipo: TipoOcorrencia = watch('tipo')
    const feriadoNaSemana = watch('feriadoNaSemana')

    return (
        <Grid container spacing={1} columnSpacing={2}>
            <Grid item xs={5}>
                <FormField
                    name="codigo"
                    label="Código"
                    placeholder="(gerar automaticamente)"
                    required
                    type="number"
                    InputProps={{
                        endAdornment: (
                            <IconeAjuda>
                                Código da ocorrência. Caso esteja em branco, o
                                sistema irá gerar um código automaticamente. Se
                                optar por informar um código, este não pode ter
                                sido utilizado anteriormente.
                            </IconeAjuda>
                        ),
                    }}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    min={1}
                    max={9999}
                />
            </Grid>
            <Grid item xs={7}>
                <FormField name="descricao" required label="Nome" />
            </Grid>
            <Grid item xs={12}>
                <FormField
                    name="tipo"
                    label="Tipo"
                    defaultValue={TipoOcorrencia.AFASTAMENTO}
                    select
                    required
                    InputProps={{
                        endAdornment: (
                            <IconeAjuda className="mr-4">
                                Define como o sistema irá tratar os lançamentos
                                desta ocorrência.
                                <br />
                                <br />
                                <strong>Afastamento:</strong> Todas as faltas e
                                horas a trabalhar do dia serão zeradas. O
                                funcionário não pode trabalhar no dia.
                                <br />
                                <br />
                                <strong>Folga:</strong> Todas as faltas e horas
                                a trabalhar do dia serão zeradas. O funcionário
                                pode trabalhar no dia e receberá horas extras.
                                <br />
                                <br />
                                <strong>Compensação:</strong> Horas a trabalhar
                                do dia continuam normais, faltas não serão
                                zeradas. Pode ou não pagar o vale alimentação.
                                Se houver feriado na semana, as horas extras
                                informadas poderão ser acrescidas no dia da
                                compensação.
                                <br />
                                <br />
                                <strong>Amamentação:</strong> Se as faltas do
                                dia forem menores ou iguais ao tempo de
                                amamentação, as faltas serão zeradas. Caso
                                contrário, as faltas serão reduzidas do tempo de
                                amamentação.
                            </IconeAjuda>
                        ),
                    }}
                >
                    <Menu.Item value={TipoOcorrencia.AFASTAMENTO}>
                        Afastamento
                    </Menu.Item>
                    <Menu.Item value={TipoOcorrencia.COMPENSACAO}>
                        Compensação
                    </Menu.Item>
                    <Menu.Item value={TipoOcorrencia.AMAMENTACAO}>
                        Amamentação
                    </Menu.Item>
                    <Menu.Item value={TipoOcorrencia.FOLGA}>Folga</Menu.Item>
                </FormField>
            </Grid>
            {[TipoOcorrencia.COMPENSACAO, TipoOcorrencia.AMAMENTACAO].includes(
                tipo as any,
            ) && (
                <Grid item xs={12}>
                    <Divider className="select-none">
                        Configuração do tipo de ocorrência
                    </Divider>
                </Grid>
            )}
            {match(tipo)
                .with(TipoOcorrencia.COMPENSACAO, () => [
                    <Grid item xs={12} key="feriadoNaSemana">
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyItems="center"
                            position="relative"
                        >
                            <FormFieldCheckbox
                                name="feriadoNaSemana"
                                label="Feriado já compensado na semana"
                                defaultChecked={false}
                            />
                            <IconeAjuda>
                                Caso houver lançamento no dia de feriado, haverá
                                o acréscimo das horas extras informadas
                            </IconeAjuda>
                            {feriadoNaSemana ? (
                                <FormField
                                    name="qntHrsCompensadasNaSemana"
                                    label="Quantidade de Horas Extras Acrescidas no dia"
                                    type="time"
                                    required
                                    defaultValue="04:00"
                                    sx={{
                                        width: '18rem',
                                        ml: 'auto',
                                        position: 'absolute',
                                        right: 0,
                                        mt: 2,
                                    }}
                                />
                            ) : null}
                        </Box>
                    </Grid>,
                    <Grid item xs={12} key="pagarVale">
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyItems="center"
                        >
                            <FormFieldCheckbox
                                name="pagarVale"
                                label="Pagar vale alimentação"
                                defaultChecked={false}
                            />
                            <IconeAjuda>
                                Se marcado, o sistema irá pagar o valor do vale
                                alimentação no dia da compensação.
                            </IconeAjuda>
                        </Box>
                    </Grid>,
                ])
                .with(TipoOcorrencia.AMAMENTACAO, () => (
                    <Grid item xs={5}>
                        <FormField
                            name="tempoAmamentacao"
                            type="time"
                            label="Tempo de amamentação"
                            required
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <IconeAjuda>
                                        <strong>
                                            Este campo controla o tempo máximo
                                            de amamentação por jornada que será
                                            considerado como tempo de trabalho.
                                        </strong>
                                        <br />
                                        <br />O artigo 396 da CLT regulamenta
                                        que a empregada tem direito a dois
                                        intervalos de 30 minutos cada
                                        (totalizando uma hora por jornada),
                                        destinados para amamentar o seu filho(a)
                                        até que ele(a) complete seis meses de
                                        idade. O intervalo pode ser negociado
                                        entre a empresa e a empregada.
                                    </IconeAjuda>
                                ),
                            }}
                        />
                    </Grid>
                ))
                .otherwise(() => null)}
            <Grid item xs={12}>
                <Divider className="select-none">
                    <div className="inline-flex items-center">
                        Relatórios{' '}
                        <IconeAjuda>
                            Essas configurações se aplicam ao relatório de
                            absenteísmo e ao relatório de horas trabalhadas.
                        </IconeAjuda>
                    </div>
                </Divider>
            </Grid>
            <Grid item xs={6} className="inline-flex items-center">
                <FormFieldCheckbox
                    name="somarAusenciasJustificadas"
                    label="Somar ausências justificadas"
                />
                <IconeAjuda>
                    Caso marcado, essa ocorrência vai adicionar ao campo
                    "Ausências justificadas" no relatório de absenteísmo sempre
                    que houver um lançamento no dia.
                </IconeAjuda>
            </Grid>
            <Grid item xs={6} className="inline-flex items-center">
                <FormFieldCheckbox
                    name="somarAusenciasInjustificadas"
                    label="Somar ausências injustificadas"
                />
                <IconeAjuda>
                    Caso marcado, essa ocorrência vai adicionar ao campo
                    "Ausências injustificadas" no relatório de absenteísmo
                    sempre que houver um lançamento no dia.
                </IconeAjuda>
            </Grid>
            <Grid item xs={6} className="inline-flex items-center">
                <FormFieldCheckbox
                    name="somarHorasTrabalhadas"
                    label="Somar horas trabalhadas"
                />
                <IconeAjuda>
                    Caso marcado, essa ocorrência vai adicionar ao campo "Horas
                    trabalhadas" o valor de faltas do dia em que a ocorrência
                    foi lançada.
                </IconeAjuda>
            </Grid>
        </Grid>
    )
}
